import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMoneyBillWave, FaClipboardList, FaUsers, FaFileUpload, FaBell, FaUserPlus, FaRunning, FaImages, FaCreditCard, FaBuilding, FaCalendarAlt, FaUserTie, FaNewspaper, FaCog, FaChartBar } from 'react-icons/fa';
import { MdSportsSoccer } from 'react-icons/md';
import Header from './Header';
import { Link } from 'react-router-dom';
import { authService } from './services/authService';
import api from './services/apiService';
import { academyPaymentsPath, academySettingsPath, academyTreasurerPath, academiesForRole, getCachedAcademies, setCachedAcademies, ACADEMY_ADMIN_API, ACADEMY_ROLE_ADMIN, ACADEMY_ROLE_SECRETARY, ACADEMY_ROLE_TREASURER } from './utils/academyAccess';
import './scss/admin.scss';

function Admin() {
  const navigate = useNavigate();
  const isSuperAdmin = authService.isSuperAdmin();
  const userType = sessionStorage.getItem('user_type');
  const isParent = userType === 'parent';
  const [canManageCoaches, setCanManageCoaches] = useState(
    authService.isSuperAdmin() || authService.isClubSecretary()
  );
  const [academies, setAcademies] = useState(getCachedAcademies());
  
  // Check if the team has training enabled
  const teamHasTraining = sessionStorage.getItem('selected_team_has_training');
  const hasTraining = teamHasTraining === 'true' || teamHasTraining === '1';
  const teamType = sessionStorage.getItem('selected_team_type');
  const showPayments = teamType !== 'squad' && teamType !== 'academy';

  useEffect(() => {
    if (authService.isSuperAdmin() || authService.isClubSecretary()) {
      setCanManageCoaches(true);
      return;
    }

    if (!authService.isManager()) {
      return;
    }

    const loadManagerAccess = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/get_my_manager_info.php`);
        if (response.data.success && response.data.manager_type === 'Club Secretary') {
          sessionStorage.setItem('manager_type', 'Club Secretary');
          setCanManageCoaches(true);
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadManagerAccess();
  }, []);

  useEffect(() => {
    const loadAcademies = async () => {
      try {
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_my_academy_subscriptions.php`);
        const list = response.data?.success ? (response.data.academies || []) : [];
        setAcademies(list);
        if (!isSuperAdmin) {
          setCachedAcademies(list);
        }
      } catch (err) {
        setAcademies([]);
      }
    };
    loadAcademies();
  }, [isSuperAdmin]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const parentOnlyAdmin = isParent && !authService.isManager() && !isSuperAdmin;
  const settingsAcademies = academiesForRole(academies, ACADEMY_ROLE_ADMIN);
  const paymentAcademies = academiesForRole(academies, ACADEMY_ROLE_SECRETARY);
  const treasurerAcademies = academiesForRole(academies, ACADEMY_ROLE_TREASURER);
  const academyTileList = [
    settingsAcademies.length > 0 && {
      key: 'settings',
      label: 'Academy Settings',
      icon: FaBuilding,
      path: academySettingsPath(settingsAcademies),
    },
    paymentAcademies.length > 0 && {
      key: 'payments',
      label: 'Parent payments',
      icon: FaMoneyBillWave,
      path: academyPaymentsPath(paymentAcademies),
    },
    treasurerAcademies.length > 0 && {
      key: 'treasurer',
      label: 'Treasurer Reports',
      icon: FaChartBar,
      path: academyTreasurerPath(treasurerAcademies),
    },
  ].filter(Boolean);
  const academyTiles = academyTileList.length > 0 && (
    <div className={`stats-container dashboard-nav items-${academyTileList.length}`}>
      {academyTileList.map((tile) => {
        const Icon = tile.icon;
        return (
          <div key={tile.key} className="stats-column" onClick={() => handleNavigate(tile.path)}>
            <Icon size={50} />
            <p>{tile.label}</p>
          </div>
        );
      })}
    </div>
  );

  if (parentOnlyAdmin) {
    return (
      <div className="Admin container">
        <Header />
        <div>
          <h1>Admin Dashboard</h1>
        </div>
        {academyTiles || (
          <p>No academy settings are assigned to this account.</p>
        )}
      </div>
    );
  }

  return (
    <div className="Admin container">
        <Header />
        <div>
          <h1>Admin Dashboard</h1>
        </div>
        {hasTraining && (
          <div className="stats-container dashboard-nav">
            <div className="stats-column full-width" onClick={() => handleNavigate('/createTrainingSession')}>
              <FaRunning size={50} />
              <p>Create Training Session</p>
            </div>
          </div>
        )}
        <div className="stats-container dashboard-nav">
          <div className="stats-column" onClick={() => handleNavigate('/players')}>
            <FaUsers size={50} />
            <p>Manage Players</p>
          </div>
          { authService.isSuperAdmin() && (
            <div className="stats-column" onClick={() => handleNavigate('/team-directory')}>
              <FaBuilding size={50} />
              <p>Team Directory</p>
            </div>
          )}
        </div>
        {(showPayments || authService.isSuperAdmin()) && (
          <div className="stats-container dashboard-nav">
            {showPayments && (
              <div className="stats-column" onClick={() => handleNavigate('/payments')}>
                <FaFileUpload size={50} />
                <p>Payments</p>
              </div>
            )}
            {authService.isSuperAdmin() && (
              <div className="stats-column" onClick={() => handleNavigate('/fees')}>
                <FaCreditCard size={50} />
                <p>Fees</p>
              </div>
            )}
          </div>
        )}
        <div className="stats-container dashboard-nav">
          <div className="stats-column" onClick={() => handleNavigate('/season-management')}>
            <FaCalendarAlt size={50} />
            <p>Season Management</p>
          </div>
          <div className="stats-column" onClick={() => handleNavigate('/team-settings')}>
            <FaCog size={50} />
            <p>Settings</p>
          </div>
        </div>
        {canManageCoaches && !authService.isSuperAdmin() && (
          <div className="stats-container dashboard-nav">
            <div className="stats-column" onClick={() => handleNavigate('/assign-manager')}>
              <FaUsers size={50} />
              <p>Manage Coaches</p>
            </div>
          </div>
        )}
        {isSuperAdmin && (
          <div className="stats-container dashboard-nav">
            <div className="stats-column" onClick={() => handleNavigate('/stories-manager')}>
              <FaImages size={50} />
              <p>Stories Manager</p>
            </div>
            <div className="stats-column" onClick={() => handleNavigate('/product-news')}>
              <FaNewspaper size={50} />
              <p>Product News</p>
            </div>
          </div>
        )}
        { authService.isSuperAdmin() && (
          <>
            <div className="stats-container dashboard-nav">
              <div className="stats-column" onClick={() => handleNavigate('/activity-log')}>
                <FaClipboardList size={50} />
                <p>Activity Log</p>
              </div>
            </div>
            <div className="stats-container dashboard-nav">
              <div className="stats-column" onClick={() => handleNavigate('/register-team')}>
                <FaUserPlus size={50} />
                <p>Register Team</p>
              </div>
              <div className="stats-column" onClick={() => handleNavigate('/assign-manager')}>
                <FaUsers size={50} />
                <p>Assign Manager / Coach</p>
              </div>
              <div className="stats-column" onClick={() => handleNavigate('/assign-club-secretary')}>
                <FaUserTie size={50} />
                <p>Assign Club Secretary</p>
              </div>
            </div>
            <div className="stats-container dashboard-nav items-2">
              <div className="stats-column" onClick={() => handleNavigate('/stripe-admin')}>
                <FaCreditCard size={50} />
                <p>Pro subscriptions</p>
              </div>
              <div className="stats-column" onClick={() => handleNavigate('/academy-subscriptions')}>
                <FaBuilding size={50} />
                <p>Create an Academy</p>
              </div>
            </div>
            <div className="stats-container dashboard-nav items-2">
              <div className="stats-column" onClick={() => handleNavigate(academySettingsPath(academies))}>
                <FaCog size={50} />
                <p>Academy Settings</p>
              </div>
              <div className="stats-column" onClick={() => handleNavigate(academyTreasurerPath(academies))}>
                <FaChartBar size={50} />
                <p>Treasurer Reports</p>
              </div>
            </div>
          </>
        )}
        {!isSuperAdmin && academyTiles}
      
        {/* <div className="admin-controls">
          <button onClick={() => navigate('/player-matches')} className="admin-button">
            <FaUsers />
            Player Matches & Payments
          </button>
        </div> */}
      </div>
  );
}

export default Admin; 